'use client';

import {createSelectableContext} from '@utils/selectableContext';
import {usePathname} from 'next/navigation';
import {PropsWithChildren, useCallback, useEffect, useRef} from 'react';

import {
  AdFoxScriptState,
  AdFoxStatsEvent,
  AdPlatformType,
  TEmitYmEvent,
  TLogTmrEvent,
} from '../types';
import {Controller} from './parts/Controller';

export type YandexAdsContextType = {
  logTmrEvent: TLogTmrEvent;
  emitYmEvent: TEmitYmEvent;

  activeAdsTypes: Array<AdPlatformType>;
  addActiveAdsType: (value: AdPlatformType) => void;

  scriptState: AdFoxScriptState;
  setScriptState: (value: AdFoxScriptState) => void;

  statsUrl: string;
  emitAdFoxEvent: (item: AdFoxStatsEvent) => void;
  consumeAdFoxEvents: () => AdFoxStatsEvent[];

  getActiveAppPackageName: () => string | null;
  getUserVkId: () => number | null;

  adsDisabled: boolean;
};

type ProviderProps = PropsWithChildren<{
  statsUrl: string;
  /** список паснеймов, где реклама показана быть не должна */
  pathnamesBlacklist?: string[];
  logTmrEvent: TLogTmrEvent;
  emitYmEvent: TEmitYmEvent;
  getActiveAppPackageName?: () => string | null;
  getUserVkId?: () => number | null;
}>;

const noop = () => {
  // noop
};

const {Context, useContextValueSelector, useCreateContextValue} =
  createSelectableContext<YandexAdsContextType>({
    logTmrEvent: noop,
    emitYmEvent: noop,

    activeAdsTypes: [],
    addActiveAdsType: noop,

    scriptState: 'not_initialized',
    setScriptState: noop,

    statsUrl: '',
    emitAdFoxEvent: noop,
    consumeAdFoxEvents: () => [],

    getActiveAppPackageName: () => null,
    getUserVkId: () => -1,

    adsDisabled: false,
  });

export const YandexAdsContextProvider = ({
  logTmrEvent,
  emitYmEvent,
  getActiveAppPackageName,
  getUserVkId,
  statsUrl,
  pathnamesBlacklist,
  children,
}: ProviderProps) => {
  const adFoxStatsEvents = useRef<AdFoxStatsEvent[]>([]);

  const emitAdFoxEvent = useCallback((event: AdFoxStatsEvent) => {
    adFoxStatsEvents.current.push(event);
  }, []);

  const consumeAdFoxEvents = useCallback(() => {
    const accumulatedEvents = [...adFoxStatsEvents.current];
    adFoxStatsEvents.current = [];
    return accumulatedEvents;
  }, []);

  const contextValue = useCreateContextValue({
    logTmrEvent,
    emitYmEvent,

    activeAdsTypes: [],
    addActiveAdsType: (adType) => {
      const currentState = contextValue.getState();
      if (currentState.activeAdsTypes.includes(adType)) {
        return;
      }
      contextValue.replaceState({
        ...currentState,
        activeAdsTypes: [...currentState.activeAdsTypes, adType],
      });
    },

    scriptState: 'not_initialized',
    setScriptState: (scriptState) => {
      contextValue.replaceState({...contextValue.getState(), scriptState});
    },

    statsUrl,
    emitAdFoxEvent,
    consumeAdFoxEvents,

    getActiveAppPackageName: getActiveAppPackageName || (() => null),
    getUserVkId: getUserVkId || (() => -1),
    adsDisabled: false,
  });

  const pathname = usePathname();

  useEffect(() => {
    // тут используем не pathname а из документа,
    // чтобы при заполении тоггла не надо было думать про basePath, реврайты локалей и прочее
    const pathname = document.location.pathname;
    const isBlacklistedPathname = !!pathnamesBlacklist?.includes(pathname);
    const currentState = contextValue.getState();
    if (isBlacklistedPathname !== currentState.adsDisabled) {
      if (isBlacklistedPathname) {
        console.warn(
          `Ads context - disabling ads due to pathname ${pathname} being blacklisted`,
        );
      } else {
        console.warn(`Ads context - enabling ads`);
      }
      contextValue.replaceState({
        ...currentState,
        adsDisabled: isBlacklistedPathname,
      });
    }
  }, [pathname, contextValue, pathnamesBlacklist]);

  return (
    <Context.Provider value={contextValue}>
      {children}
      <Controller />
    </Context.Provider>
  );
};

export const useYandexAdsContextSelector = useContextValueSelector;

export const YandexAdsContextCommonSelectors = {
  getFullState: (state: YandexAdsContextType) => state,
  getScriptState: (state: YandexAdsContextType) => state.scriptState,
  getActiveAdTypes: (state: YandexAdsContextType) => state.activeAdsTypes,
  getEmitAdFoxEvent: (state: YandexAdsContextType) => state.emitAdFoxEvent,
  getAdsDisabled: (state: YandexAdsContextType) => state.adsDisabled,
};
